import { RadioGroup } from "@tine/designsystem-ui-react";
import { observer } from "mobx-react-lite";
import { type ChangeEvent, useRef, useState } from "react";

import { sendSignup } from "../../common/tracking";
import type { ContentfulContestForm } from "../../common/types/cmsTypes";
import { formatDate } from "../../common/utils/dateUtils";
import authStore from "../../stores/auth/authStore";
import promotionStore from "../../stores/promotionStore";
import uiStore from "../../stores/uiStore";
import Button from "../Button";
import ContestFormCorrectAnswer from "./ContestFormCorrectAnswer";
import RichTextRenderer from "./RichTextRenderer";
import { useContestAvailability } from "./useContestAvailability";

type ContestFormProps = {
   id: string;
   // Contentful also accepts date-only strings, which the SDK's date type does not include.
   entry: {
      fields: Omit<ContentfulContestForm["fields"], "validFrom" | "validTo"> & { validFrom?: string; validTo?: string };
   };
   trackingFormName?: string;
};

// Now, ContestForm is only used by AdventCalendarSlots
const ContestQuestion = observer(({ entry, trackingFormName }: ContestFormProps) => {
   const options = entry.fields.options.map((option) => ({
      label: option,
      value: option
   }));
   const company = authStore.getCurrentCompany();
   const companyName = company?.name || "Ditt selskap";

   const [answer, setAnswer] = useState<string | undefined>(options[0].value);
   const [hasAnswered, setHasAnswered] = useState<boolean>(false);
   const [signingUp, setSigningUp] = useState(false);
   const [submitted, setSubmitted] = useState(false);
   const submittingRef = useRef(false);
   const buttonRef = useRef<HTMLButtonElement>(null);
   const { isNotAvailableYet, isExpired, canSubmit } = useContestAvailability(entry.fields.validFrom, entry.fields.validTo);
   // Keep the animation source mounted until the signup request completes.
   const isSignedUp = submitted || (!signingUp && promotionStore.isSignedUpTo(entry.fields.signUpCode));

   const storeAnswer = (e: ChangeEvent<HTMLInputElement>) => {
      setAnswer(e.target.value);
      setHasAnswered(false);
   };

   const signup = async () => {
      if (submittingRef.current || isSignedUp || !authStore.isLoggedIn() || !canSubmit()) {
         return;
      }
      submittingRef.current = true;
      setSigningUp(true);
      try {
         const signedUp = await promotionStore.signUpToPromotion(entry.fields.signUpCode, "", "", {});
         if (!signedUp) {
            return;
         }
         if (buttonRef.current) {
            uiStore.addFlyingItem(buttonRef, "advent-calendar-sticky");
         }
         sendSignup(trackingFormName);
         setSubmitted(true);
      } finally {
         submittingRef.current = false;
         setSigningUp(false);
      }
   };

   const checkAnswer = async () => {
      setHasAnswered(true);
      if (answer === entry.fields.correctOption) {
         await signup();
      }
   };

   if (isNotAvailableYet) {
      return (
         <div>
            <h3>Dette spørsmålet er ikke tilgjengelig enda.</h3>
            <p>Du kan delta på denne f.o.m. {formatDate(entry.fields.validFrom, true, true, false)}.</p>
         </div>
      );
   }

   if (isExpired) {
      const message = isSignedUp ? entry.fields.messageNotValidParticipated : entry.fields.messageNotValidNotParticipated;
      return message ? (
         <RichTextRenderer document={message} context="advent-calendar" />
      ) : (
         <p>Denne konkurransen er avsluttet.</p>
      );
   }

   return (
      <div className="flex flex-col gap-4">
         <h2 className="text-xl">{entry.fields.question}</h2>

         {isSignedUp || (hasAnswered && answer === entry.fields.correctOption) ? (
            <ContestFormCorrectAnswer
               answer={entry.fields.correctOption}
               companyName={companyName}
               isSignedUp={isSignedUp}
               signingUp={signingUp}
               onSignup={signup}
               buttonRef={buttonRef}
            />
         ) : (
            <>
               <RadioGroup label="Velg et alternativ:" options={options} onChange={storeAnswer} className="pb-2" />
               {hasAnswered && answer !== entry.fields.correctOption && <h2>Feil svar. Prøv igjen!</h2>}
               <div className="flex flex-row justify-center">
                  <Button
                     variant="secondary"
                     onClick={checkAnswer}
                     disabled={signingUp || hasAnswered || isSignedUp}
                     ref={buttonRef}
                  >
                     Sjekk svar
                  </Button>
               </div>
            </>
         )}
      </div>
   );
});

const ContestForm = observer((props: ContestFormProps) => (
   <ContestQuestion key={`${authStore.currentCompany}:${props.id}:${props.entry.fields.signUpCode}`} {...props} />
));

export default ContestForm;
